import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StickyMobileCTA } from "@/components/StickyMobileCTA";
import AmbientBackground from "@/components/AmbientBackground";
import { TrailingSlashRedirect } from "@/components/TrailingSlashRedirect";
import { Suspense, lazy } from "react";

// Critical pages loaded immediately
import Home from "./pages/Home";
import Websites from "./pages/Websites";
import NotFound from "./pages/NotFound";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

// Lazy load non-critical pages for code splitting
const GrowthAddOns = lazy(() => import("./pages/GrowthAddOns"));
const GoogleAds = lazy(() => import("./pages/GoogleAds"));
const GBPOptimization = lazy(() => import("./pages/GBPOptimization"));
const LawnCareWebsiteDesign = lazy(() => import("./pages/LawnCareWebsiteDesign"));
const LawnCareWebsiteExamples = lazy(() => import("./pages/LawnCareWebsiteExamples"));
const LandscapingWebsiteExamples = lazy(() => import("./pages/LandscapingWebsiteExamples"));
const ServiceAreaPagesLocalSEO = lazy(() => import("./pages/ServiceAreaPagesLocalSEO"));
const ServicePagesThatRank = lazy(() => import("./pages/ServicePagesThatRank"));
const LawnCareWebsiteCost = lazy(() => import("./pages/LawnCareWebsiteCost"));
const LandscapingWebsiteCost = lazy(() => import("./pages/LandscapingWebsiteCost"));
const LandscapingWebsiteDesign = lazy(() => import("./pages/LandscapingWebsiteDesign"));
const LawnCareQuoteForm = lazy(() => import("./pages/LawnCareQuoteForm"));
const LandscapingQuoteForm = lazy(() => import("./pages/LandscapingQuoteForm"));
const LawnCareWebsiteContent = lazy(() => import("./pages/LawnCareWebsiteContent"));
const LandscapingWebsiteContent = lazy(() => import("./pages/LandscapingWebsiteContent"));
const LawnCareWebsiteSEO = lazy(() => import("./pages/LawnCareWebsiteSEO"));
const LandscapingWebsiteSEO = lazy(() => import("./pages/LandscapingWebsiteSEO"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Resources = lazy(() => import("./pages/Resources"));
const LawnCareWebsiteChecklist = lazy(() => import("./pages/resources/LawnCareWebsiteChecklist"));
const GoogleBusinessProfileForLawnCare = lazy(() => import("./pages/resources/GoogleBusinessProfileForLawnCare"));
const LawnCareLeadGeneration = lazy(() => import("./pages/resources/LawnCareLeadGeneration"));
const LawnCarePostcards = lazy(() => import("./pages/resources/LawnCarePostcards"));
const LawnCareReferralPrograms = lazy(() => import("./pages/resources/LawnCareReferralPrograms"));
const LawnCareSocialMediaCalendar = lazy(() => import("./pages/resources/LawnCareSocialMediaCalendar"));
const LawnCareYardSigns = lazy(() => import("./pages/resources/LawnCareYardSigns"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const LawnCareWebsiteDesignPricing = lazy(() => import("./pages/LawnCareWebsiteDesignPricing"));
const LandscapingWebsiteDesignPricing = lazy(() => import("./pages/LandscapingWebsiteDesignPricing"));
const LawnCareWebsiteRedesign = lazy(() => import("./pages/LawnCareWebsiteRedesign"));
const LandscapingPortfolioWebsite = lazy(() => import("./pages/LandscapingPortfolioWebsite"));
const LawnCareWebsiteSEOServices = lazy(() => import("./pages/LawnCareWebsiteSEOServices"));
const LawnCareWebDesignAgency = lazy(() => import("./pages/LawnCareWebDesignAgency"));
const LandscapingWebDesignAgency = lazy(() => import("./pages/LandscapingWebDesignAgency"));
const LawnCareWebsiteDesigner = lazy(() => import("./pages/LawnCareWebsiteDesigner"));
const LandscapingWebsiteDesigner = lazy(() => import("./pages/LandscapingWebsiteDesigner"));
const LawnCareWebsiteMaintenance = lazy(() => import("./pages/LawnCareWebsiteMaintenance"));
const LandscapingWebsiteMaintenance = lazy(() => import("./pages/LandscapingWebsiteMaintenance"));
const LawnCareWebsiteHosting = lazy(() => import("./pages/LawnCareWebsiteHosting"));
const LandscapingWebsiteHosting = lazy(() => import("./pages/LandscapingWebsiteHosting"));
const LawnCareWebsiteCopywriting = lazy(() => import("./pages/LawnCareWebsiteCopywriting"));
const LandscapingWebsiteCopywriting = lazy(() => import("./pages/LandscapingWebsiteCopywriting"));
const ThankYou = lazy(() => import("./pages/ThankYou"));

// New SEO and Trust pages
const LawnCareSEO = lazy(() => import("./pages/LawnCareSEO"));
const AuthorBrandonCho = lazy(() => import("./pages/AuthorBrandonCho"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// New pillar pages
const LawnCareMarketing = lazy(() => import("./pages/LawnCareMarketing"));
const GoogleAdsLawnCare = lazy(() => import("./pages/GoogleAdsLawnCare"));
const LawnCareKeywords = lazy(() => import("./pages/LawnCareKeywords"));

// Website Audit pages
const LawnCareWebsiteAudit = lazy(() => import("./pages/LawnCareWebsiteAudit"));
const LandscapingWebsiteAudit = lazy(() => import("./pages/LandscapingWebsiteAudit"));

// Comparison pages
const ComparisonsHub = lazy(() => import("./pages/comparisons/ComparisonsHub"));
const WixVsWordpressLawnCare = lazy(() => import("./pages/comparisons/WixVsWordpressLawnCare"));
const SquarespaceVsWordpressLandscaping = lazy(() => import("./pages/comparisons/SquarespaceVsWordpressLandscaping"));
const WordpressVsBuilderLawnCare = lazy(() => import("./pages/comparisons/WordpressVsBuilderLawnCare"));
const BestWebsiteBuilderLawnCare = lazy(() => import("./pages/comparisons/BestWebsiteBuilderLawnCare"));
const BestWebsiteBuilderLandscapers = lazy(() => import("./pages/comparisons/BestWebsiteBuilderLandscapers"));
const AgencyVsFreelancer = lazy(() => import("./pages/comparisons/AgencyVsFreelancer"));
const CheapVsProfessional = lazy(() => import("./pages/comparisons/CheapVsProfessional"));
const OneTimeVsSubscription = lazy(() => import("./pages/comparisons/OneTimeVsSubscription"));
const SEOvsAds = lazy(() => import("./pages/comparisons/SEOvsAds"));
const WebsiteVsWebsitePlusGBP = lazy(() => import("./pages/comparisons/WebsiteVsWebsitePlusGBP"));
const BestCRMForms = lazy(() => import("./pages/comparisons/BestCRMForms"));
const CallTrackingTools = lazy(() => import("./pages/comparisons/CallTrackingTools"));

// Website fixes pages
const WebsiteFixesHub = lazy(() => import("./pages/website-fixes/WebsiteFixesHub"));
const LawnCareNotGettingLeads = lazy(() => import("./pages/website-fixes/LawnCareNotGettingLeads"));
const LandscapingNotGettingLeads = lazy(() => import("./pages/website-fixes/LandscapingNotGettingLeads"));
const LawnCareSlowMobile = lazy(() => import("./pages/website-fixes/LawnCareSlowMobile"));
const LandscapingWebsiteSlow = lazy(() => import("./pages/website-fixes/LandscapingWebsiteSlow"));
const QuoteFormNotSubmitting = lazy(() => import("./pages/website-fixes/QuoteFormNotSubmitting"));
const LandscapingQuoteFormConversion = lazy(() => import("./pages/website-fixes/LandscapingQuoteFormConversion"));
const ServicePagesNotRanking = lazy(() => import("./pages/website-fixes/ServicePagesNotRanking"));
const ServiceAreaPagesNotRanking = lazy(() => import("./pages/website-fixes/ServiceAreaPagesNotRanking"));
const NoCallsFromGoogleMaps = lazy(() => import("./pages/website-fixes/NoCallsFromGoogleMaps"));
const HighTrafficNoCalls = lazy(() => import("./pages/website-fixes/HighTrafficNoCalls"));
const StickyCallButton = lazy(() => import("./pages/website-fixes/StickyCallButton"));
const AddReviewsToWebsite = lazy(() => import("./pages/website-fixes/AddReviewsToWebsite"));

// Website templates pages
const WebsiteTemplatesHub = lazy(() => import("./pages/website-templates/WebsiteTemplatesHub"));
const LawnCareHomepageTemplate = lazy(() => import("./pages/website-templates/LawnCareHomepageTemplate"));
const LandscapingHomepageTemplate = lazy(() => import("./pages/website-templates/LandscapingHomepageTemplate"));
const LawnCareServicePageTemplate = lazy(() => import("./pages/website-templates/LawnCareServicePageTemplate"));
const LandscapingServicePageTemplate = lazy(() => import("./pages/website-templates/LandscapingServicePageTemplate"));
const ServiceAreaPageTemplate = lazy(() => import("./pages/website-templates/ServiceAreaPageTemplate"));
const LawnCareWebsiteSEOChecklist = lazy(() => import("./pages/website-templates/LawnCareWebsiteSEOChecklist"));
const LandscapingWebsiteSEOChecklist = lazy(() => import("./pages/website-templates/LandscapingWebsiteSEOChecklist"));
const LawnCareQuoteFormTemplate = lazy(() => import("./pages/website-templates/LawnCareQuoteFormTemplate"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <TrailingSlashRedirect />
          <AmbientBackground />
          <div className="flex flex-col min-h-screen pb-[60px] md:pb-0 relative z-10">
            <Header />
            <StickyMobileCTA />
            <main className="flex-1">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/websites" element={<Websites />} />
                  <Route path="/growth-add-ons" element={<GrowthAddOns />} />
                  <Route path="/growth-add-ons/google-ads" element={<GoogleAds />} />
                  <Route path="/growth-add-ons/gbp" element={<GBPOptimization />} />
                  <Route path="/lawn-care-website-design" element={<LawnCareWebsiteDesign />} />
                  <Route path="/lawn-care-website-examples" element={<LawnCareWebsiteExamples />} />
                  <Route path="/landscaping-website-examples" element={<LandscapingWebsiteExamples />} />
                  <Route path="/service-area-pages-local-seo" element={<ServiceAreaPagesLocalSEO />} />
                  <Route path="/service-pages-that-rank" element={<ServicePagesThatRank />} />
                  <Route path="/lawn-care-website-cost" element={<LawnCareWebsiteCost />} />
                  <Route path="/landscaping-website-cost" element={<LandscapingWebsiteCost />} />
                  <Route path="/landscaping-website-design" element={<LandscapingWebsiteDesign />} />
                  <Route path="/lawn-care-quote-form" element={<LawnCareQuoteForm />} />
                  <Route path="/landscaping-quote-form" element={<LandscapingQuoteForm />} />
                  <Route path="/lawn-care-website-content" element={<LawnCareWebsiteContent />} />
                  <Route path="/landscaping-website-content" element={<LandscapingWebsiteContent />} />
                  <Route path="/lawn-care-website-seo" element={<LawnCareWebsiteSEO />} />
                  <Route path="/landscaping-website-seo" element={<LandscapingWebsiteSEO />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/resources" element={<Resources />} />
                  <Route path="/resources/lawn-care-website-checklist" element={<LawnCareWebsiteChecklist />} />
                  <Route path="/resources/lawn-care-lead-generation" element={<LawnCareLeadGeneration />} />
                  <Route path="/resources/lawn-care-postcards" element={<LawnCarePostcards />} />
                  <Route path="/resources/lawn-care-referral-programs" element={<LawnCareReferralPrograms />} />
                  <Route path="/resources/lawn-care-social-media-calendar" element={<LawnCareSocialMediaCalendar />} />
                  <Route path="/resources/lawn-care-yard-signs" element={<LawnCareYardSigns />} />
                  <Route path="/resources/google-business-profile-for-lawn-care" element={<GoogleBusinessProfileForLawnCare />} />
                  {/* Legacy redirect: old lead gen URL to new pillar page */}
                  <Route path="/resources/how-to-get-more-lawn-care-leads-online" element={<LawnCareLeadGeneration />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/lawn-care-website-design-pricing" element={<LawnCareWebsiteDesignPricing />} />
                  <Route path="/landscaping-website-design-pricing" element={<LandscapingWebsiteDesignPricing />} />
                  <Route path="/lawn-care-website-redesign" element={<LawnCareWebsiteRedesign />} />
                  <Route path="/landscaping-portfolio-website" element={<LandscapingPortfolioWebsite />} />
                  <Route path="/lawn-care-website-seo-services" element={<LawnCareWebsiteSEOServices />} />
                  <Route path="/lawn-care-web-design-agency" element={<LawnCareWebDesignAgency />} />
                  <Route path="/landscaping-web-design-agency" element={<LandscapingWebDesignAgency />} />
                  <Route path="/lawn-care-website-designer" element={<LawnCareWebsiteDesigner />} />
                  <Route path="/landscaping-website-designer" element={<LandscapingWebsiteDesigner />} />
                  <Route path="/lawn-care-website-maintenance" element={<LawnCareWebsiteMaintenance />} />
                  <Route path="/landscaping-website-maintenance" element={<LandscapingWebsiteMaintenance />} />
                  <Route path="/lawn-care-website-hosting" element={<LawnCareWebsiteHosting />} />
                  <Route path="/landscaping-website-hosting" element={<LandscapingWebsiteHosting />} />
                  <Route path="/lawn-care-website-copywriting" element={<LawnCareWebsiteCopywriting />} />
                  <Route path="/landscaping-website-copywriting" element={<LandscapingWebsiteCopywriting />} />
                  
                  {/* Comparison pages */}
                  <Route path="/comparisons" element={<ComparisonsHub />} />
                  <Route path="/comparisons/wix-vs-wordpress-lawn-care" element={<WixVsWordpressLawnCare />} />
                  <Route path="/comparisons/squarespace-vs-wordpress-landscaping" element={<SquarespaceVsWordpressLandscaping />} />
                  <Route path="/comparisons/wordpress-vs-website-builder-lawn-care" element={<WordpressVsBuilderLawnCare />} />
                  <Route path="/comparisons/best-website-builder-lawn-care" element={<BestWebsiteBuilderLawnCare />} />
                  <Route path="/comparisons/best-website-builder-landscapers" element={<BestWebsiteBuilderLandscapers />} />
                  <Route path="/comparisons/agency-vs-freelancer" element={<AgencyVsFreelancer />} />
                  <Route path="/comparisons/cheap-vs-professional-website" element={<CheapVsProfessional />} />
                  <Route path="/comparisons/one-time-vs-subscription-website" element={<OneTimeVsSubscription />} />
                  <Route path="/comparisons/seo-vs-google-ads" element={<SEOvsAds />} />
                  <Route path="/comparisons/website-vs-website-plus-gbp" element={<WebsiteVsWebsitePlusGBP />} />
                  <Route path="/comparisons/best-crm-forms-lawn-care" element={<BestCRMForms />} />
                  <Route path="/comparisons/call-tracking-tools" element={<CallTrackingTools />} />
                  
                  {/* Website fixes pages */}
                  <Route path="/website-fixes" element={<WebsiteFixesHub />} />
                  <Route path="/website-fixes/lawn-care-website-not-getting-leads" element={<LawnCareNotGettingLeads />} />
                  <Route path="/website-fixes/landscaping-website-not-getting-leads" element={<LandscapingNotGettingLeads />} />
                  <Route path="/website-fixes/lawn-care-website-slow-mobile" element={<LawnCareSlowMobile />} />
                  <Route path="/website-fixes/landscaping-website-slow" element={<LandscapingWebsiteSlow />} />
                  <Route path="/website-fixes/quote-form-not-submitting" element={<QuoteFormNotSubmitting />} />
                  <Route path="/website-fixes/landscaping-quote-form-conversion" element={<LandscapingQuoteFormConversion />} />
                  <Route path="/website-fixes/service-pages-not-ranking" element={<ServicePagesNotRanking />} />
                  <Route path="/website-fixes/service-area-pages-not-ranking" element={<ServiceAreaPagesNotRanking />} />
                  <Route path="/website-fixes/no-calls-from-google-maps" element={<NoCallsFromGoogleMaps />} />
                  <Route path="/website-fixes/high-traffic-no-calls" element={<HighTrafficNoCalls />} />
                  <Route path="/website-fixes/sticky-call-button" element={<StickyCallButton />} />
                  <Route path="/website-fixes/add-reviews-to-website" element={<AddReviewsToWebsite />} />
                  
                  {/* Website templates pages */}
                  <Route path="/website-templates" element={<WebsiteTemplatesHub />} />
                  <Route path="/website-templates/lawn-care-homepage-template" element={<LawnCareHomepageTemplate />} />
                  <Route path="/website-templates/landscaping-homepage-template" element={<LandscapingHomepageTemplate />} />
                  <Route path="/website-templates/lawn-care-service-page-template" element={<LawnCareServicePageTemplate />} />
                  <Route path="/website-templates/landscaping-service-page-template" element={<LandscapingServicePageTemplate />} />
                  <Route path="/website-templates/service-area-page-template" element={<ServiceAreaPageTemplate />} />
                  <Route path="/website-templates/lawn-care-website-seo-checklist" element={<LawnCareWebsiteSEOChecklist />} />
                  <Route path="/website-templates/landscaping-website-seo-checklist" element={<LandscapingWebsiteSEOChecklist />} />
                  <Route path="/website-templates/lawn-care-quote-form-template" element={<LawnCareQuoteFormTemplate />} />
                  
                  {/* Thank you page (noindex) */}
                  <Route path="/thank-you" element={<ThankYou />} />
                  
                  {/* SEO and Trust pages */}
                  <Route path="/lawn-care-seo" element={<LawnCareSEO />} />
                  <Route path="/lawn-care-marketing" element={<LawnCareMarketing />} />
                  <Route path="/google-ads-lawn-care" element={<GoogleAdsLawnCare />} />
                  <Route path="/lawn-care-keywords" element={<LawnCareKeywords />} />
                  <Route path="/author/brandon-cho" element={<AuthorBrandonCho />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  
                  {/* Website Audit pages */}
                  <Route path="/lawn-care-website-audit" element={<LawnCareWebsiteAudit />} />
                  <Route path="/landscaping-website-audit" element={<LandscapingWebsiteAudit />} />
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
